import { Spacer } from '@chakra-ui/react';
import {
  CustomerPortalPaymentMethod,
  PaymentMethodTypeEnum,
} from '@monetize/types/app';
import { MdAccountBalance, MdCreditCard, MdMoreVert } from 'react-icons/md';
import {
  MBadge,
  MBox,
  MFlex,
  MIcon,
  MText,
} from '../../../../../components/Monetize';
import MDropdownActions, {
  MDropdownActionItem,
  MIconAction,
} from '../../../../../components/Monetize/MDropdownActions';
import { PaymentMethodTypeEnumDisplay } from '../../../../../constants/paymentMethods';

interface PaymentMethodRowProps {
  paymentMethod: CustomerPortalPaymentMethod;
  isDefault: boolean;
  canSetAsDefault: boolean;
  isSettingDefault: boolean;
  onSetAsDefault: (paymentMethodId: string) => void;
}

const getPaymentIcon = (type: PaymentMethodTypeEnum) => {
  switch (type) {
    case PaymentMethodTypeEnum.CREDIT_CARD:
      return MdCreditCard;
    case PaymentMethodTypeEnum.DIRECT_DEBIT:
      return MdAccountBalance;
    default:
      return MdCreditCard;
  }
};

export const PaymentMethodRow = ({
  paymentMethod,
  isDefault,
  canSetAsDefault,
  isSettingDefault,
  onSetAsDefault,
}: PaymentMethodRowProps) => {
  const {
    bankName,
    expirationMonth,
    expirationYear,
    paymentMethodType,
    paymentMethodName,
  } = paymentMethod;

  const displayName =
    paymentMethodType === PaymentMethodTypeEnum.CREDIT_CARD
      ? paymentMethod.creditCardType
      : bankName;

  const showExpiration =
    paymentMethodType === PaymentMethodTypeEnum.CREDIT_CARD &&
    expirationYear &&
    expirationYear > 0;

  const lastFour = paymentMethod.lastFour
    ? `ending in ${paymentMethod.lastFour}`
    : '';

  return (
    <MFlex
      alignItems="center"
      gap={3}
      p={3}
      borderRadius="md"
      border="1px solid"
      borderColor="tGray.back"
      bg="white"
      transition="all 0.15s ease"
    >
      <MBox
        p={2}
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <MIcon
          as={getPaymentIcon(paymentMethodType)}
          color="tGray.darkPurple"
          boxSize={5}
        />
      </MBox>
      <MBox flex={1}>
        <MFlex justifyContent="space-between" alignItems="center">
          <MFlex alignItems="center" gap={2}>
            <MText fontWeight="600" fontSize="sm">
              {paymentMethodName}
            </MText>
            <Spacer />
            {isDefault && (
              <MBadge
                colorPalette="green"
                variant="subtle"
                fontWeight="bold"
                fontSize="2xs"
                px={2}
                py={0.5}
                borderRadius="full"
                textTransform="uppercase"
                letterSpacing="0.5px"
              >
                Default
              </MBadge>
            )}
          </MFlex>
          <MFlex alignItems="center">
            <MText color="tGray.darkPurple" fontSize="sm">
              {displayName} {lastFour}
            </MText>
          </MFlex>
        </MFlex>
        <MFlex justifyContent="space-between" alignItems="center" mt={0.5}>
          <MText fontSize="xs" color="tGray.darkPurple">
            {PaymentMethodTypeEnumDisplay[paymentMethodType]}
          </MText>
          {showExpiration && expirationMonth && expirationYear && (
            <MText fontSize="xs" color="tGray.darkPurple">
              Exp {expirationMonth?.toString().padStart(2, '0')}/
              {expirationYear}
            </MText>
          )}
        </MFlex>
      </MBox>
      {canSetAsDefault && (
        <MDropdownActions
          renderTrigger={() => <MIconAction icon={MdMoreVert} />}
          menuButtonProps={{ 'aria-label': 'Payment method actions' }}
          isDisabled={isSettingDefault}
        >
          <MDropdownActionItem
            onClick={() => onSetAsDefault(paymentMethod.id)}
            isDisabled={isSettingDefault}
          >
            Set as default
          </MDropdownActionItem>
        </MDropdownActions>
      )}
    </MFlex>
  );
};
